import type { FC, MouseEvent, ReactNode } from 'react'
import type { AccountSummary } from '@/types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { BrandLogo } from '@/components/generals/brand-logo'
import { BookButton } from '@/components/landing/booking-provider'
import { clinic, navLinks } from '@/constants/clinic'

import {
    CalendarPlus,
    ChevronRight,
    Menu,
    MessageCircle,
    Phone,
    UserRound
} from 'lucide-react'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

const EmergencyBar: FC = (): ReactNode => {
    return (
        <div className='bg-ink text-white'>
            <div className='mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-[13px] sm:px-6 lg:px-8'>
                <p className='flex min-w-0 items-center gap-2.5 text-white/80'>
                    <span className='relative flex size-2 shrink-0' aria-hidden='true'>
                        <span className='absolute inline-flex size-full animate-pulse-ring rounded-full bg-rose-400 motion-reduce:animate-none' />
                        <span className='relative inline-flex size-2 rounded-full bg-rose-500' />
                    </span>

                    <span className='truncate'>
                        <span className='font-semibold text-white'>
                            UGD & Persalinan 24 jam
                        </span>

                        <span className='hidden sm:inline'>
                            , datang langsung tanpa janji
                        </span>
                    </span>
                </p>

                <div className='flex shrink-0 items-center gap-5'>
                    <a
                        href={clinic.phone.href}
                        className='inline-flex items-center gap-1.5 font-semibold text-white transition-colors hover:text-brand-200'
                    >
                        <Phone className='size-3.5' aria-hidden='true' />
                        <span className='tabular-nums'>
                            {clinic.phone.label}
                        </span>
                    </a>

                    <a
                        href={clinic.whatsapp.href}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='hidden items-center gap-1.5 text-white/80 transition-colors hover:text-white md:inline-flex'
                    >
                        <MessageCircle className='size-3.5' aria-hidden='true' />
                        WhatsApp
                        {' '}
                        <span className='tabular-nums'>
                            {clinic.whatsapp.label}
                        </span>
                    </a>
                </div>
            </div>
        </div>
    )
}

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [activeId, setActiveId] = useState<string | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [account, setAccount] = useState<AccountSummary | null>(null)
    const pendingTarget = useRef<{ id: string, href: string } | null>(null)

    useEffect(() => {
        let active = true

        fetch('/api/auth/sesi', { cache: 'no-store' })
            .then(response => (response.ok ? response.json() : null))
            .then((data: { account?: AccountSummary | null } | null) => {
                if (active) setAccount(data?.account ?? null)
            })
            .catch(() => {
                // Signed-out header stays as is.
            })

        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        const handleScroll = (): void => setScrolled(window.scrollY > 40)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        const sections = navLinks
            .map(link => document.getElementById(link.id))
            .filter((section): section is HTMLElement => section !== null)

        if (sections.length === 0) return

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                const id = entry.target.id

                if (entry.isIntersecting) setActiveId(id)
                else setActiveId(current => (current === id ? null : current))
            }
        }, { rootMargin: '-40% 0px -55% 0px' })

        sections.forEach(section => observer.observe(section))

        return () => observer.disconnect()
    }, [])

    const handleMobileLink = (event: MouseEvent<HTMLAnchorElement>, id: string, href: string): void => {
        event.preventDefault()
        pendingTarget.current = { id, href }
        setMenuOpen(false)
    }

    const handleMenuClosed = (open: boolean): void => {
        if (open || !pendingTarget.current) return

        const { id, href } = pendingTarget.current
        const target = document.getElementById(id)

        pendingTarget.current = null

        if (!target) {
            window.location.assign(href)
            return
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        window.history.replaceState(null, '', `#${id}`)
    }

    return (
        <>
            <EmergencyBar />

            <header
                className={cn(
                    'sticky top-0 z-40 border-b transition-[background-color,box-shadow,border-color] duration-300',
                    scrolled
                        ? 'border-border/80 bg-white/85 shadow-lg shadow-ink/5 backdrop-blur-xl'
                        : 'border-transparent bg-white/70 backdrop-blur-md'
                )}
            >
                <div className='mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8'>
                    <Link
                        href='/'
                        className='rounded-full outline-none focus-visible:ring-4 focus-visible:ring-ring/30'
                        aria-label={`${clinic.name}, ke beranda`}
                    >
                        <BrandLogo />
                    </Link>

                    <nav aria-label='Navigasi utama' className='hidden lg:block'>
                        <ul className='flex items-center gap-1'>
                            {navLinks.map(link => (
                                <li key={link.id}>
                                    <a
                                        href={link.href}
                                        aria-current={activeId === link.id ? 'location' : undefined}
                                        className={cn(
                                            'rounded-full px-3 py-2 text-sm font-semibold transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/30 xl:px-4',
                                            activeId === link.id
                                                ? 'bg-brand-50 text-brand-800'
                                                : 'text-ink-soft hover:bg-muted hover:text-ink'
                                        )}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2'>
                        {account ? (
                            <Link
                                href='/masuk'
                                className='hidden h-10 max-w-48 items-center gap-2 rounded-full px-3 text-sm font-semibold text-ink ring-1 ring-border transition-colors outline-none hover:bg-muted focus-visible:ring-4 focus-visible:ring-ring/30 lg:inline-flex'
                            >
                                <UserRound className='size-4 shrink-0 text-brand-600' aria-hidden='true' />
                                <span data-mdlfy-cms={`akun_pasien:${account.id}:nama_lengkap`} className='truncate'>
                                    {account.nama}
                                </span>
                            </Link>
                        ) : (
                            <>
                                <Link href='/masuk' className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'hidden h-10 lg:inline-flex')}>
                                    Masuk
                                </Link>

                                <Link href='/daftar' className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'hidden h-10 xl:inline-flex')}>
                                    Daftar
                                </Link>
                            </>
                        )}

                        <BookButton size='sm' className='hidden h-10 px-5 sm:inline-flex'>
                            <CalendarPlus className='size-4' aria-hidden='true' />
                            Buat Janji
                        </BookButton>

                        <Sheet open={menuOpen} onOpenChange={setMenuOpen} onOpenChangeComplete={handleMenuClosed}>
                            <SheetTrigger className='inline-flex size-10 items-center justify-center rounded-full text-ink ring-1 ring-border transition-colors outline-none hover:bg-muted focus-visible:ring-4 focus-visible:ring-ring/30 lg:hidden'>
                                <Menu className='size-5' aria-hidden='true' />
                                <span className='sr-only'>
                                    Buka menu
                                </span>
                            </SheetTrigger>

                            <SheetContent side='right' className='gap-0 p-0'>
                                <div className='border-b border-border px-5 py-5'>
                                    <SheetTitle className='sr-only'>
                                        Menu
                                    </SheetTitle>

                                    <SheetDescription className='sr-only'>
                                        Navigasi halaman dan kontak klinik
                                    </SheetDescription>

                                    <BrandLogo />
                                </div>

                                <nav aria-label='Navigasi seluler' className='flex-1 overflow-y-auto px-3 py-4'>
                                    <ul className='grid gap-1'>
                                        {navLinks.map(link => (
                                            <li key={link.id}>
                                                <a
                                                    href={link.href}
                                                    onClick={event => handleMobileLink(event, link.id, link.href)}
                                                    className='flex items-center justify-between rounded-2xl px-4 py-3.5 text-base font-semibold text-ink transition-colors outline-none hover:bg-muted focus-visible:ring-4 focus-visible:ring-ring/30'
                                                >
                                                    {link.label}
                                                    <ChevronRight className='size-4 text-muted-foreground' aria-hidden='true' />
                                                </a>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className='mt-4 px-1'>
                                        {account ? (
                                            <Link
                                                href='/masuk'
                                                className='flex items-center gap-3 rounded-2xl p-3 ring-1 ring-border transition-colors outline-none hover:bg-muted focus-visible:ring-4 focus-visible:ring-ring/30'
                                            >
                                                <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700'>
                                                    <UserRound className='size-5' aria-hidden='true' />
                                                </span>

                                                <span className='min-w-0 text-sm'>
                                                    <span className='block text-muted-foreground'>
                                                        Masuk sebagai
                                                    </span>

                                                    <span data-mdlfy-cms={`akun_pasien:${account.id}:nama_lengkap`} className='block truncate font-semibold text-ink'>
                                                        {account.nama}
                                                    </span>
                                                </span>
                                            </Link>
                                        ) : (
                                            <div className='grid grid-cols-2 gap-2'>
                                                <Link href='/masuk' className={buttonVariants({ variant: 'outline' })}>
                                                    Masuk
                                                </Link>

                                                <Link href='/daftar' className={buttonVariants({ variant: 'soft' })}>
                                                    Daftar
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </nav>

                                <div className='grid gap-3 border-t border-border p-5'>
                                    <a
                                        href={clinic.phone.href}
                                        className='flex items-center gap-3 rounded-2xl bg-rose-50 p-4 text-rose-800 ring-1 ring-rose-100 ring-inset'
                                    >
                                        <Phone className='size-5 shrink-0' aria-hidden='true' />

                                        <span className='text-sm'>
                                            <span className='block font-bold'>
                                                Darurat 24 jam
                                            </span>

                                            <span className='tabular-nums'>
                                                {clinic.phone.label}
                                            </span>
                                        </span>
                                    </a>

                                    <a
                                        href={clinic.whatsapp.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='flex items-center gap-3 rounded-2xl bg-brand-50 p-4 text-brand-800 ring-1 ring-brand-100 ring-inset'
                                    >
                                        <MessageCircle className='size-5 shrink-0' aria-hidden='true' />

                                        <span className='text-sm'>
                                            <span className='block font-bold'>
                                                Chat WhatsApp
                                            </span>

                                            <span className='tabular-nums'>
                                                {clinic.whatsapp.label}
                                            </span>
                                        </span>
                                    </a>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>
        </>
    )
}
